import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlatformService } from '@rma/generic/ng/util';
import { ReplaySubject, startWith } from 'rxjs';
import { ContextStorageService } from './context-storage.service';
import { CurrentUser } from './domain/current-user.models';
import { SelectedContext } from './domain/profile-context.models';
import { InitialContextService } from './initial-context.service';

/**
 * ContextSelectorService
 *
 * Manages the selection and persistence of the user's current profile context (agent or agency) within the application.
 *
 */
@Injectable({
  providedIn: 'root',
})
export class ContextSelectorService {
  private readonly contextStorage = inject(ContextStorageService);
  private readonly initialContextService = inject(InitialContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformService = inject(PlatformService);

  private readonly selectedContext = new ReplaySubject<SelectedContext | undefined>(1);
  public readonly selectedContext$ = this.selectedContext.asObservable();

  public initialise(user: CurrentUser | null) {
    // On the server there is no localStorage or query string to resolve a context from, and consumers rely on
    // selectedContext$ staying silent during SSR (so the user/profile context streams do not emit server-side).
    if (this.platformService.isPlatformServer) {
      return;
    }

    this.selectedContext.pipe(startWith(), takeUntilDestroyed(this.destroyRef)).subscribe((context) => {
      if (context) {
        this.contextStorage.setContext(context);
      } else {
        this.contextStorage.removeContext();
      }
    });

    const initialContext = this.initialContextService.getInitialContext(user);

    this.setContext(initialContext);
  }

  public setDefaultContext(user: CurrentUser) {
    const context = this.initialContextService.getDefaultContext(user);
    this.setContext(context);
  }

  public setContext(context: SelectedContext | undefined): void {
    return this.selectedContext.next(context);
  }
}
